/**
 * ISO 15118 Plug & Charge tester store (v2.0.1-only).
 *
 * Drives the three CP→CSMS PnC exchanges with tester-supplied artifacts and
 * surfaces the real CSMS verdict — the simulator is a faithful charging-station
 * pipe, it does NOT fake crypto:
 *   - authorizePnC          → Authorize(idToken type eMAID, + certificate/hashData)
 *   - get15118Certificate   → Get15118EVCertificate (EXI Install/Update relay)
 *   - getCertificateStatus  → GetCertificateStatus (OCSP)
 *
 * The live adapter comes from useStationStore.activeV201Adapter(); each action
 * is a no-op-with-error when no 2.0.1 station is connected.
 */
import { create } from 'zustand';
import { errorMessage } from '@/lib/errorMessage';
import type {
  Get15118EVCertificateDomain,
  Get15118EVCertificateResponse,
  GetCertificateStatusDomain,
  GetCertificateStatusResponse,
} from '@/protocol/v201/actions/pnc';
import type {
  PncAuthorizeDomain,
  PncAuthorizeResult,
} from '@/protocol/v201/actions/authorize';
import { useStationStore } from './useStationStore';

// Re-export the PnC wire types so the (ui-layer) panel imports them from the
// state layer rather than reaching into protocol/ directly (layer boundary).
export type {
  OcspRequestData,
  Get15118EVCertificateDomain,
  GetCertificateStatusDomain,
} from '@/protocol/v201/actions/pnc';
export type { PncAuthorizeDomain, PncAuthorizeResult } from '@/protocol/v201/actions/authorize';

type Status = 'idle' | 'pending' | 'done' | 'error';

export interface PlugAndChargeStore {
  readonly status: Status;
  readonly error: string | null;
  readonly authResult: PncAuthorizeResult | null;
  readonly certResult: Get15118EVCertificateResponse | null;
  readonly ocspResult: GetCertificateStatusResponse | null;
  authorizePnC(req: PncAuthorizeDomain): Promise<void>;
  get15118Certificate(req: Get15118EVCertificateDomain): Promise<void>;
  getCertificateStatus(req: GetCertificateStatusDomain): Promise<void>;
  reset(): void;
}

const NO_ADAPTER = 'Connect an OCPP 2.0.1 station first (Plug & Charge is v2.0.1-only).';

export const usePlugAndChargeStore = create<PlugAndChargeStore>((set) => ({
  status: 'idle',
  error: null,
  authResult: null,
  certResult: null,
  ocspResult: null,

  async authorizePnC(req): Promise<void> {
    const adapter = useStationStore.getState().activeV201Adapter();
    if (!adapter) {
      set({ status: 'error', error: NO_ADAPTER });
      return;
    }
    set({ status: 'pending', error: null, authResult: null });
    try {
      const authResult = await adapter.authorizePnC(req).response;
      set({ status: 'done', authResult });
    } catch (err) {
      set({ status: 'error', error: errorMessage(err) });
    }
  },

  async get15118Certificate(req): Promise<void> {
    const adapter = useStationStore.getState().activeV201Adapter();
    if (!adapter) {
      set({ status: 'error', error: NO_ADAPTER });
      return;
    }
    set({ status: 'pending', error: null, certResult: null });
    try {
      const certResult = await adapter.get15118EVCertificate(req).response;
      set({ status: 'done', certResult });
    } catch (err) {
      set({ status: 'error', error: errorMessage(err) });
    }
  },

  async getCertificateStatus(req): Promise<void> {
    const adapter = useStationStore.getState().activeV201Adapter();
    if (!adapter) {
      set({ status: 'error', error: NO_ADAPTER });
      return;
    }
    set({ status: 'pending', error: null, ocspResult: null });
    try {
      const ocspResult = await adapter.getCertificateStatus(req).response;
      set({ status: 'done', ocspResult });
    } catch (err) {
      set({ status: 'error', error: errorMessage(err) });
    }
  },

  reset(): void {
    set({ status: 'idle', error: null, authResult: null, certResult: null, ocspResult: null });
  },
}));
